<template>
  <div class="filter-selector-menu">
    <div class="filter-body">
      <div v-if="allFilters.length === 0" class="empty-state">
        Select a field to start filtering.
      </div>

      <div v-for="(filter, index) in allFilters" :key="index" class="filter-row" :class="{ 'ghost-row': !filter.field }">
        <SelectInput
          :modelValue="filter.field"
          @update:modelValue="handleFieldChange(index, $event)"
          :options="getAvailableFieldOptions(filter.field)"
          placeholder="Select field..."
          class="field-select"
        />

        <SelectInput
          v-if="filter.field"
          :modelValue="filter.operator"
          @update:modelValue="updateFilterValue(index, 'operator', $event)"
          :options="getOperatorOptions(filter.field)"
          placeholder="Select..."
          class="operator-select"
        />

        <div class="filter-inputs">
          <template v-if="filter.field && filter.operator">
            <!-- Date inputs with range -->
            <template v-if="getFieldType(filter.field) === 'date'">
              <RangeInput v-if="filter.operator === 'between'">
                <template #from>
                  <DateInput
                    :modelValue="filter.from"
                    @update:modelValue="updateFilterValueNoEmit(index, 'from', $event)"
                    @blur="emitFilters"
                    placeholder="MM/DD/YYYY"
                  />
                </template>
                <template #to>
                  <DateInput
                    :modelValue="filter.to"
                    @update:modelValue="updateFilterValueNoEmit(index, 'to', $event)"
                    @blur="emitFilters"
                    placeholder="MM/DD/YYYY"
                  />
                </template>
              </RangeInput>
              <DateInput
                v-else-if="filter.operator === 'after'"
                :modelValue="filter.from"
                @update:modelValue="updateFilterValueNoEmit(index, 'from', $event)"
                @blur="emitFilters"
                placeholder="MM/DD/YYYY"
              />
              <DateInput
                v-else-if="filter.operator === 'before'"
                :modelValue="filter.to"
                @update:modelValue="updateFilterValueNoEmit(index, 'to', $event)"
                @blur="emitFilters"
                placeholder="MM/DD/YYYY"
              />
              <DateInput
                v-else-if="filter.operator === 'is'"
                :modelValue="filter.value"
                @update:modelValue="updateFilterValueNoEmit(index, 'value', $event)"
                @blur="emitFilters"
                placeholder="MM/DD/YYYY"
              />
            </template>

            <!-- Currency inputs with range -->
            <template v-else-if="getFieldType(filter.field) === 'currency'">
              <RangeInput v-if="filter.operator === 'between'">
                <template #from>
                  <MonetaryInput
                    :modelValue="filter.from"
                    @update:modelValue="updateFilterValueNoEmit(index, 'from', $event)"
                    @blur="emitFilters"
                    :optional="true"
                    placeholder="$0.00"
                  />
                </template>
                <template #to>
                  <MonetaryInput
                    :modelValue="filter.to"
                    @update:modelValue="updateFilterValueNoEmit(index, 'to', $event)"
                    @blur="emitFilters"
                    :optional="true"
                    placeholder="$0.00"
                  />
                </template>
              </RangeInput>
              <MonetaryInput
                v-else-if="filter.operator === 'gte'"
                :modelValue="filter.from"
                @update:modelValue="updateFilterValueNoEmit(index, 'from', $event)"
                @blur="emitFilters"
                :optional="true"
                placeholder="$0.00"
              />
              <MonetaryInput
                v-else-if="filter.operator === 'lte'"
                :modelValue="filter.to"
                @update:modelValue="updateFilterValueNoEmit(index, 'to', $event)"
                @blur="emitFilters"
                :optional="true"
                placeholder="$0.00"
              />
            </template>

            <!-- Select input -->
            <SelectInput
              v-else-if="getFieldType(filter.field) === 'select'"
              :modelValue="filter.value"
              @update:modelValue="updateFilterValue(index, 'value', $event)"
              :options="getFieldOptions(filter.field)"
              placeholder="Select..."
            />

            <!-- Multiselect input -->
            <SelectInput
              v-else-if="getFieldType(filter.field) === 'multiselect'"
              :modelValue="filter.values"
              @update:modelValue="updateFilterValue(index, 'values', $event)"
              :options="getFieldOptions(filter.field)"
              :multiSelect="true"
              placeholder="Select..."
            />

            <!-- Number inputs with range -->
            <template v-else-if="getFieldType(filter.field) === 'number'">
              <RangeInput v-if="filter.operator === 'between'">
                <template #from>
                  <NumericalInput
                    :modelValue="filter.from"
                    @update:modelValue="updateFilterValueNoEmit(index, 'from', $event)"
                    @blur="emitFilters"
                    placeholder="From"
                  />
                </template>
                <template #to>
                  <NumericalInput
                    :modelValue="filter.to"
                    @update:modelValue="updateFilterValueNoEmit(index, 'to', $event)"
                    @blur="emitFilters"
                    placeholder="To"
                  />
                </template>
              </RangeInput>
              <NumericalInput
                v-else-if="filter.operator === 'gte'"
                :modelValue="filter.from"
                @update:modelValue="updateFilterValueNoEmit(index, 'from', $event)"
                @blur="emitFilters"
                placeholder="Minimum"
              />
              <NumericalInput
                v-else-if="filter.operator === 'lte'"
                :modelValue="filter.to"
                @update:modelValue="updateFilterValueNoEmit(index, 'to', $event)"
                @blur="emitFilters"
                placeholder="Maximum"
              />
            </template>
          </template>
        </div>

        <button
          v-if="filter.field"
          class="button remove-button"
          @click.prevent="removeFilter(index)"
          title="Remove filter"
        >
          <CloseIcon :size="16" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CloseIcon from 'vue-material-design-icons/Close.vue';
import SelectInput from '../inputs/SelectInput.vue';
import DateInput from '../inputs/DateInput.vue';
import MonetaryInput from '../inputs/MonetaryInput.vue';
import NumericalInput from '../inputs/NumericalInput.vue';
import RangeInput from '../inputs/RangeInput.vue';
import { getLocalToday } from '@/utils/dateUtils';

export default {
  name: 'FilterSelectorMenu',
  components: {
    CloseIcon,
    SelectInput,
    DateInput,
    MonetaryInput,
    NumericalInput,
    RangeInput
  },
  props: {
    selectedFilters: {
      type: Array,
      default: () => []
    },
    fieldOptions: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      localFilters: []
    };
  },
  computed: {
    fieldOptionsList() {
      return this.fieldOptions.map(field => ({
        value: field.id,
        label: field.label
      }));
    },
    allFilters() {
      const filters = [...this.localFilters];
      const hasGhostRow = filters.some(f => !f.field);
      if (!hasGhostRow) {
        filters.push({
          id: Date.now() + Math.random(),
          field: '',
          operator: '',
          from: null,
          to: null
        });
      }
      return filters;
    },
    usedFieldIds() {
      return this.localFilters
        .filter(f => f.field)
        .map(f => f.field);
    }
  },
  watch: {
    selectedFilters: {
      immediate: true,
      handler(newFilters) {
        this.localFilters = JSON.parse(JSON.stringify(newFilters));
      }
    }
  },
  methods: {
    getAvailableFieldOptions(currentField) {
      return this.fieldOptionsList.map(option => ({
        ...option,
        disabled: this.usedFieldIds.includes(option.value) && option.value !== currentField
      }));
    },
    getOperatorOptions(fieldId) {
      const fieldType = this.getFieldType(fieldId);

      if (fieldType === 'date') {
        return [
          { value: 'is', label: 'is' },
          { value: 'after', label: 'after' },
          { value: 'before', label: 'before' },
          { value: 'between', label: 'between' }
        ];
      } else if (fieldType === 'currency' || fieldType === 'number') {
        return [
          { value: 'gte', label: 'at least' },
          { value: 'lte', label: 'at most' },
          { value: 'between', label: 'between' }
        ];
      } else if (fieldType === 'select') {
        return [
          { value: 'is', label: 'is' }
        ];
      } else if (fieldType === 'multiselect') {
        return [
          { value: 'any', label: 'is any' },
          { value: 'all', label: 'is all' }
        ];
      }

      return [];
    },
    getFieldOptions(fieldId) {
      const field = this.fieldOptions.find(f => f.id === fieldId);
      return field?.options || [];
    },
    removeFilter(index) {
      this.localFilters = this.localFilters.filter((_, i) => i !== index);
      this.emitFilters();
    },
    handleFieldChange(index, value) {
      const isGhostRow = index >= this.localFilters.length;
      const fieldType = this.getFieldType(value);

      let newFilter = {
        id: Date.now() + Math.random(),
        field: value,
        operator: '',
        from: null,
        to: null
      };

      // Initialize based on field type
      if (fieldType === 'select') {
        newFilter = {
          id: Date.now() + Math.random(),
          field: value,
          operator: 'is',
          value: null
        };
      } else if (fieldType === 'multiselect') {
        newFilter = {
          id: Date.now() + Math.random(),
          field: value,
          operator: 'any', // Default to 'any' for multiselect
          values: []
        };
      }

      if (isGhostRow) {
        this.localFilters = [...this.localFilters, newFilter];
        // Always emit when adding a new filter, even without operator
        this.emitFilters();
      } else {
        // Create a new array with the updated filter to ensure reactivity
        const updatedFilters = [...this.localFilters];
        updatedFilters[index] = newFilter;
        this.localFilters = updatedFilters;
        // Emit immediately when swapping fields so old filter is removed from URL
        this.emitFilters();
      }
    },
    updateFilterValue(index, key, value) {
      const updatedFilters = [...this.localFilters];
      const filter = updatedFilters[index];

      // If changing operator to 'is' for a date field, default to today's date
      if (key === 'operator' && value === 'is' && this.getFieldType(filter.field) === 'date') {
        const today = getLocalToday();
        updatedFilters[index] = {
          ...filter,
          operator: value,
          value: today,
          from: null,
          to: null
        };
      } else {
        updatedFilters[index] = {
          ...filter,
          [key]: value
        };
      }

      this.localFilters = updatedFilters;
      this.emitFilters();
    },
    updateFilterValueNoEmit(index, key, value) {
      const updatedFilters = [...this.localFilters];
      updatedFilters[index] = {
        ...updatedFilters[index],
        [key]: value
      };
      this.localFilters = updatedFilters;
    },
    getFieldType(fieldId) {
      const field = this.fieldOptions.find(f => f.id === fieldId);
      return field?.type || null;
    },
    emitFilters() {
      this.$emit('update:filters', this.localFilters);
    }
  },
  emits: ['update:filters']
}
</script>

<style scoped>
.filter-selector-menu {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: var(--border-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.filter-body {
  padding: 12px;
  max-height: 400px;
  overflow-y: auto;
}

.empty-state {
  padding: 16px 8px;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.filter-row {
  display: grid;
  grid-template-columns: 175px 100px 250px auto;
  gap: 6px;
  align-items: center;
  padding: 0;
  margin-bottom: 6px;
}

.filter-row.ghost-row {
  /* No special styling for ghost rows */
}

.field-select {
  width: 100%;
}

.operator-select {
  width: 100%;
}

.filter-inputs {
  display: flex;
  gap: 6px;
  align-items: center;
  min-height: 31px;
}

.remove-button {
  padding: 3px;
  min-width: 31px;
  height: 31px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-button:hover {
  background: #fee;
  border-color: #faa;
}
</style>
